/**
 * User-facing copy for Cognito software-token (TOTP) failures.
 *
 * Cognito reports a wrong or stale six-digit code under several different
 * exception names depending on whether it arrives during enrolment
 * (VerifySoftwareToken) or sign-in (RespondToAuthChallenge), and the raw
 * messages are unhelpful ("Invalid session provided"). Map them once.
 */

function errorName(err: unknown): string {
  if (!err || typeof err !== 'object') return ''
  return 'name' in err ? String((err as { name?: string }).name) : ''
}

function errorMessage(err: unknown): string {
  if (!err || typeof err !== 'object') return ''
  return 'message' in err ? String((err as { message?: string }).message) : ''
}

/** The six digits did not match — the user can simply try the next code. */
export function isTotpCodeMismatch(err: unknown): boolean {
  const name = errorName(err)
  const message = errorMessage(err)
  return (
    name === 'EnableSoftwareTokenMFAException' ||
    name === 'CodeMismatchException' ||
    /invalid code/i.test(message) ||
    /code mismatch/i.test(message)
  )
}

/**
 * The challenge or setup session expired. Both are unrecoverable in place:
 * enrolment needs a fresh secret, sign-in needs the password entered again.
 */
export function isTotpSessionExpired(err: unknown): boolean {
  const name = errorName(err)
  const message = errorMessage(err)
  return (
    name === 'NotAuthorizedException' ||
    name === 'ExpiredCodeException' ||
    /invalid session/i.test(message) ||
    /session is expired/i.test(message)
  )
}

export function totpEnrolmentErrorMessage(err: unknown): string {
  if (isTotpCodeMismatch(err)) {
    return 'That code was not accepted. Wait for your authenticator app to show the next code, then enter it — codes expire every 30 seconds.'
  }
  if (isTotpSessionExpired(err)) {
    return 'This setup attempt timed out. Start again to get a fresh QR code.'
  }
  return errorMessage(err) || 'Could not turn on your authenticator app. Try again.'
}

export function totpChallengeErrorMessage(err: unknown): string {
  if (isTotpCodeMismatch(err)) {
    return 'That code was not accepted. Enter the current six digits from your authenticator app.'
  }
  if (isTotpSessionExpired(err)) {
    return 'Your sign-in timed out. Enter your password again to get a new verification prompt.'
  }
  return errorMessage(err) || 'Could not verify that code. Try again.'
}
